import { geojson } from "flatgeobuf"
import { Feature, FeatureCollection, Geometry } from "geojson"
import { geoJSON, GeoJSON as LeafletGeoJSON, LayerGroup as LeafletLayerGroup, LeafletMouseEvent, PathOptions } from "leaflet"
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { LayerGroup } from "react-leaflet"
import { HucFeatureProperties as Huc6FeatureProperties } from "../hooks/useFetchHucs"

const HUC_10_URL = "https://storage.googleapis.com/index-of-connectivity/10_digit_wbd_simplified_v_generalize.fgb"

export interface Props {
    parentHuc: Feature | undefined
    setProps: ((feature: Huc6FeatureProperties | undefined) => void)
}

export function asFeatureCollection(features: Feature[]): FeatureCollection {
    return {
        type: "FeatureCollection",
        features
    }
}
interface HucFeatureProperties {
    huc10: string;
    loaddate: string;
    name: string;
}

// this will be removed in the future. just a band aid for now
function translateHucFeatureProperties({ huc10, loaddate, name }: HucFeatureProperties): Huc6FeatureProperties {
    return { hucId: huc10, loaddate: loaddate, name: name, states: "noop", fid: -1 }
}

// function styleHucFeatures(huc10: string | undefined, feature: Feature<Geometry, HucFeatureProperties> | undefined): PathOptions {
function styleHucFeatures(hucFeat: HucFeatureProperties | undefined, feature: Feature<Geometry, HucFeatureProperties> | undefined): PathOptions {
    const style: PathOptions = {
        color: "#3D624C",
        stroke: true,
        weight: 2,
        fillOpacity: feature?.properties.huc10 === hucFeat?.huc10 ? 0.9 : 0.1
    }
    return style
}

interface CacheState<T> {
    [key: string]: T
}

// interface UseHuc<S> {
//     huc?: S
//     feature?: Feature<Geometry, S>
//     setFeature: Dispatch<SetStateAction<Feature<Geometry, S> | undefined>>
// }

// function useHuc<S>(initialHuc: Feature<Geometry, S> | (() => Feature<Geometry, S>) | undefined): UseHuc<S> {
//     const [feature, setFeature] = useState<S | undefined>();

//     const huc = useMemo(() => { }, [feature])

//     return { huc, feature, setFeature }
// }

function getPaneName(el?: HTMLElement): string | undefined {
    if (el === undefined) return

    // leaflet-pane
    // leaflet-<name>-pane
    for (let i = 0; i < el.classList.length; i++) {
        let item = el.classList.item(i)
        if (item !== null && item !== "leaflet-pane" && item.startsWith("leaflet-") && item.endsWith("-pane")) {
            return item.slice(8, item.length - 5)
        }
    }
}

export function useHuc10s(parentHuc: Feature | undefined, ref: MutableRefObject<LeafletLayerGroup<any> | null>): HucFeatureProperties | undefined {
    const [cache, setCache] = useState<CacheState<LeafletGeoJSON<any>[]>>({})
    const [selected, setSelected] = useState<HucFeatureProperties>()
    const [_, setSelectedGeoJSON] = useState<LeafletGeoJSON>()

    // wrap geojson.deserialize in useCallback so the same AsyncGenerator is returned for each parentHuc
    const gen = useCallback(() => {
        if (parentHuc === undefined) return

        const layer = geoJSON(parentHuc)
        const bounds = layer.getBounds()
        return geojson.deserialize(HUC_10_URL, { maxX: bounds.getEast(), maxY: bounds.getNorth(), minX: bounds.getWest(), minY: bounds.getSouth() })
    }, [parentHuc])

    const paneName = useMemo(() => getPaneName(ref.current?.getPane()), [ref])

    const handleClick = useCallback((event: LeafletMouseEvent) => {
        const layer = (event.propagatedFrom as LeafletGeoJSON)
        layer.setStyle({ "fillOpacity": 0.9 })

        setSelectedGeoJSON(curr => {
            curr?.setStyle({ "fillOpacity": 0.1 })
            return layer
        })
        setSelected(event.propagatedFrom.feature.properties)
    }, [setSelected, setSelectedGeoJSON])

    useEffect(() => {
        let rendered = false;

        const parentHucId = parentHuc?.properties?.hucId as string

        const getFeatures = async () => {
            if (gen() === undefined) return

            let features: LeafletGeoJSON[] = []

            for await (const item of gen() as AsyncGenerator) {
                if (rendered) return

                const hucId = (item as Feature).properties?.huc10

                if (hucId.slice(0, parentHucId.length) !== parentHucId) {
                    continue
                }

                const feature = geoJSON(item as Feature, { style: feature => styleHucFeatures(selected, feature), pane: paneName })
                feature.on("click", handleClick)
                feature.on("remove", e => {
                    setSelectedGeoJSON(curr => {

                        curr?.setStyle({ "fillOpacity": 0.1 })
                        return undefined
                    })
                })

                ref.current?.addLayer(feature)
                features = [feature, ...features]
            }

            setCache(curr => {
                return { [parentHucId]: features, ...curr }
            })
        }

        const res = cache[parentHucId]
        if (res !== undefined) {
            // cache hit
            res.forEach(item => ref.current?.addLayer(item))
        } else {
            // cache miss
            getFeatures()
        }

        return () => { rendered = true }

    }, [gen, parentHuc, ref, cache, setCache, paneName, handleClick, selected])

    return selected
}



export default function Huc10s({ parentHuc, setProps }: Props) {
    const ref = useRef<LeafletLayerGroup | null>(null)
    useEffect(() => {
        ref.current?.clearLayers()
    }, [parentHuc])

    const feature = useHuc10s(parentHuc, ref)

    // useEffect(() => feature === undefined ? undefined : setProps(translateHucFeatureProperties(feature.properties as HucFeatureProperties)), [feature, setProps])
    useEffect(() => {
        if (feature === undefined) return

        setProps(translateHucFeatureProperties(feature))
    }
        , [feature, setProps])

    return <LayerGroup ref={ref} />
}
